import { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Loader2, LogOut } from 'lucide-react'
import { AppShell } from './components/layout/AppShell'
import { DashboardV2 } from './pages/DashboardV2'
import { Catalog } from './pages/Catalog'
import { MyProjects } from './pages/MyProjects'
import { Admin } from './pages/Admin'
import { QuickStart } from './pages/QuickStart'
import { Profile } from './pages/Profile'
import { RegistryPage } from './pages/RegistryPage'

const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
})

const API_URL = import.meta.env.VITE_API_URL ?? ''

// ─── Auth Types ────────────────────────────────────────────────────────────────

interface AuthUser {
  email: string
  name: string
  authenticated: boolean
}

type AuthState = 'loading' | 'authenticated' | 'unauthenticated'

// ─── Auth Helpers ──────────────────────────────────────────────────────────────

function signInWithOidc() {
  const redirectUri = globalThis.location.origin
  globalThis.location.href = `${API_URL}/api/v1/auth/oidc?redirect_uri=${encodeURIComponent(redirectUri)}`
}

function signOut() {
  const redirectUri = globalThis.location.origin
  globalThis.location.href = `${API_URL}/api/v1/auth/logout?redirect_uri=${encodeURIComponent(redirectUri)}`
}

async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    const res = await fetch(`${API_URL}/api/v1/auth/me`, { credentials: 'include' })
    if (!res.ok) return null
    return await res.json()
  } catch {
    return null
  }
}

// ─── Loading Screen ────────────────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh', background: 'var(--page-bg)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: 'var(--space-2)',
    }}>
      <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--brand)' }} />
      <div style={{ fontSize: 'var(--text-body2-size)', color: 'var(--text-muted)' }}>
        Authenticating with KONE SSO…
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ─── User Menu (logout button) ────────────────────────────────────────────────

export function UserMenu({ user }: Readonly<{ user: AuthUser }>) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 'var(--space-15)',
      fontSize: 'var(--text-body3-size)', color: 'var(--text-muted)',
    }}>
      <span>{user.name || user.email}</span>
      <button
        onClick={signOut}
        title="Sign out"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-muted)', display: 'flex', alignItems: 'center',
          padding: 4, borderRadius: 'var(--radius-xs)',
        }}
      >
        <LogOut size={14} />
      </button>
    </div>
  )
}

// ─── Routes ───────────────────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<AppShell />}>
        <Route index element={<DashboardV2 />} />
        <Route path="quick-start" element={<QuickStart />} />
        <Route path="mcp-registry" element={<RegistryPage />} />
        <Route path="catalog" element={<Catalog />} />
        <Route path="projects" element={<MyProjects />} />
        <Route path="profile" element={<Profile />} />
        <Route path="admin" element={<Admin />} />
        {/* Legacy redirects */}
        <Route path="playground" element={<Navigate to="/catalog?tab=playground" replace />} />
        <Route path="registry" element={<Navigate to="/catalog" replace />} />
        <Route path="delivery" element={<Navigate to="/catalog" replace />} />
        <Route path="settings" element={<Navigate to="/admin" replace />} />
      </Route>
    </Routes>
  )
}

// ─── App ──────────────────────────────────────────────────────────────────────

export function App() {
  const [authState, setAuthState] = useState<AuthState>('loading')

  useEffect(() => {
    fetchCurrentUser().then((u) => {
      if (u?.authenticated) {
        setAuthState('authenticated')
      } else {
        setAuthState('unauthenticated')
      }
    })
  }, [])

  // Auto-redirect to OIDC if not authenticated (same pattern as hoppscotch)
  useEffect(() => {
    if (authState === 'unauthenticated') {
      signInWithOidc()
    }
  }, [authState])

  if (authState === 'loading' || authState === 'unauthenticated') {
    return (
      <QueryClientProvider client={qc}>
        <LoadingScreen />
      </QueryClientProvider>
    )
  }

  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  )
}
